import React, { useCallback, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { Box, ButtonBase, Stack, Typography } from '@mui/material'
import { useSmartTranslation } from '../../../../../../generic/hooks/useSmartTranslation'
import JobStatusChip from '../JobStatusChip'
import { boundedRatio } from '../../utils/jobDetailFormat'
import { OVERLINE_SX, Pill, ProgressBar } from './JobDetailPrimitives'

// Historia 160 (CA1.13, RN4-RN6, D17, G1, mock 1) — la secuencia de jobs como NAVEGACIÓN.
// Tarjetas en una sola fila que se desplaza en horizontal (nunca se parte en filas, S13); la
// seleccionada resaltada y centrada. Los retrabajos llegan de Sun al final y llevan su marca. El
// contador "N jobs" cuenta TODOS (G1); el "paso i de N" de la cabecera, solo el flujo ideal.

const CARD_WIDTH = 252

const SequenceCard = React.memo(({ item, total, selected, onSelect, registerCard }) => {
  const { t_ } = useSmartTranslation()
  const finished = item.finished || 0
  return (
    <ButtonBase
      ref={(element) => registerCard(item.jobReferenceId, element)}
      onClick={() => onSelect(item.jobReferenceId)}
      aria-pressed={selected}
      data-testid={'sequence-card-' + item.jobReferenceId}
      data-selected={selected ? 'true' : 'false'}
      sx={{
        flex: '0 0 auto',
        width: CARD_WIDTH,
        display: 'block',
        textAlign: 'left',
        p: 1.5,
        borderRadius: 2,
        bgcolor: 'background.card',
        border: 2,
        borderColor: selected ? 'primary.main' : 'transparent',
        outline: selected ? 'none' : '1px solid',
        outlineColor: 'divider'
      }}
    >
      <Stack direction='row' justifyContent='space-between' alignItems='flex-start' spacing={1}>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 800, lineHeight: 1.2 }} noWrap>{item.code}</Typography>
          <Typography sx={{ fontWeight: 700, lineHeight: 1.2 }} noWrap title={item.name}>{item.name}</Typography>
        </Box>
        <Stack alignItems='flex-end' spacing={0.5} sx={{ flexShrink: 0 }}>
          <JobStatusChip status={item.status} statusType={item.statusType} />
          {item.rework === true ? <Pill label={t_('Rework')} tone='warning' testId={'sequence-rework-' + item.jobReferenceId} /> : null}
        </Stack>
      </Stack>
      <ProgressBar value={boundedRatio(finished, total)} sx={{ mt: 1.25 }} />
      <Typography variant='body2' color='text.secondary' sx={{ mt: 0.75 }} data-testid={'sequence-progress-' + item.jobReferenceId}>
        {finished} / {total}
      </Typography>
    </ButtonBase>
  )
})

SequenceCard.displayName = 'SequenceCard'
SequenceCard.propTypes = {
  item: PropTypes.object.isRequired,
  total: PropTypes.number,
  selected: PropTypes.bool,
  onSelect: PropTypes.func,
  registerCard: PropTypes.func
}

const JobSequenceStrip = ({ sequence = [], total = 0, selectedJobId = '', onSelect = () => {} }) => {
  const { t_ } = useSmartTranslation()
  const cardRefs = useRef({})
  const registerCard = useCallback((jobReferenceId, element) => { cardRefs.current[jobReferenceId] = element }, [])

  // CA1.5/CA1.6: la seleccionada queda visible en la tira.
  useEffect(() => {
    const element = cardRefs.current[selectedJobId]
    if (element && typeof element.scrollIntoView === 'function') {
      element.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' })
    }
  }, [selectedJobId, sequence.length])

  return (
    <Box sx={{ px: 3, py: 2 }} component='section' aria-label={t_('Job sequence')} data-testid='job-sequence'>
      <Stack direction='row' justifyContent='space-between' alignItems='center' sx={{ mb: 1.25 }}>
        <Typography component='h3' sx={OVERLINE_SX}>{t_('Job sequence')}</Typography>
        <Typography variant='body2' color='text.secondary' data-testid='sequence-count'>{t_('{{count}} jobs', { count: sequence.length })}</Typography>
      </Stack>
      <Stack
        direction='row'
        spacing={1.5}
        data-testid='sequence-track'
        sx={{ overflowX: 'auto', overflowY: 'hidden', flexWrap: 'nowrap', pb: 0.5, scrollbarWidth: 'thin' }}
      >
        {sequence.map((item) => (
          <SequenceCard
            key={item.jobReferenceId}
            item={item}
            total={total}
            selected={item.jobReferenceId === selectedJobId}
            onSelect={onSelect}
            registerCard={registerCard}
          />
        ))}
      </Stack>
    </Box>
  )
}

JobSequenceStrip.propTypes = {
  sequence: PropTypes.array,
  total: PropTypes.number,
  selectedJobId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onSelect: PropTypes.func
}

export default React.memo(JobSequenceStrip)
